<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="p-3">
      <div class="overflow-auto">
        <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
          <thead>
          <tr>
            <th class="text-capitalize-first w-25">{{ helper.trans('labels.id') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.name') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.req_count') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.active_req_count') }}</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in groups" :key="index" class="">
            <td>
              <span v-if="item.id">{{ item.id }}</span>
            </td>
            <td v-if="!item.id">
              <input type="text" v-model="item.name" class="form-control form-control-sm">
            </td>
            <td v-else-if="currentEditGroup && currentEditGroup.id === item.id">
              <input type="text" v-model="currentEditGroup.name" class="form-control form-control-sm">
            </td>
            <td v-else>{{ item.name }}</td>
            <td>{{ item.requisitesCount }}</td>
            <td>{{ item.activeRequisitesCount }}</td>
            <td class="text-end">
              <div v-if="!item.id || currentEditGroup && currentEditGroup.id === item.id">
                <button type="button" @click="saveRow(index)" class="btn btn-sm btn-outline-success text-nowrap me-2"><i class="fa fa-save"></i></button>
                <button type="button" @click="cancel(index)" class="btn btn-sm btn-outline-dark text-nowrap"><i class="fa fa-cancel"></i></button>
              </div>
              <div class="d-flex justify-content-end align-items-center" v-else>
                <a href="#" style="font-size: 150%" v-b-tooltip.hover :title="helper.trans('labels.requisites_deactivate')" @click.prevent="toggleRequisites(item.id, false)" >
                  <i class="fa fa-toggle-off text-secondary"></i>
                </a>

                <a href="#"  class="me-2 ms-2" style="font-size: 150%" v-b-tooltip.hover :title="helper.trans('labels.requisites_activate')" @click.prevent="toggleRequisites(item.id, true)" >
                  <i class="fa fa-toggle-on text-success"></i>
                </a>

                <button type="button" @click="edit(item)" class="btn btn-sm btn-outline-dark text-nowrap me-2"><i class="fa fa-pencil"></i></button>
                <button type="button" @click="remove(item)" class="btn btn-sm btn-outline-danger text-nowrap"><i class="fa fa-trash"></i></button>
              </div>

            </td>
          </tr>
          </tbody>
          <tfoot>
          <tr>
            <td colspan="5" class="text-center">
              <button type="button" @click="add()" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize-first w-100"><i class="fa fa-plus"></i> {{helper.trans('button.add')}}</button>
            </td>
          </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <b-modal v-if="isTrader || isTraderOperator"
             :title="createModalTitle"
             :modelValue="createModal"
             body-class="pt-0"
             @update:modelValue="createModal = $event"
    >
      <template #default>
        <RequisiteGroupForm ref="createForm"></RequisiteGroupForm>
      </template>
      <template #footer>
        <button v-if="isTrader || isTraderOperator" type="button" v-on:click="createModal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">{{ helper.trans('button.cancel') }}</button>
        <LoaderWrapper ref="loaderCreateFrom" :zoom="0.5">
          <button v-if="isTrader || isTraderOperator" type="button" @click="saveModal" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans('button.save') }}</button>
        </LoaderWrapper>
      </template>
    </b-modal>
  </LoaderWrapper>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import APIClient from "@/service/APIClient";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {useStore} from "vuex";
import {useNotification} from "@kyvg/vue3-notification";
import TraderRequisiteGroupDTO from "@/models/TraderRequisiteGroupDTO";
import {BModal} from "bootstrap-vue-3";
import RequisiteGroupForm from "@/components/Form/RequisiteGroupForm.vue";
import RequisiteGroupFormDTO, {defaultForm} from "@/models/form/RequisiteGroupFormDTO";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import RequisiteGroupUpdateRequestDTO from "@/models/request/RequisiteGroupUpdateRequestDTO";

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  components: {
    RequisiteGroupForm,
    BModal,
    LoaderWrapper,
  },
  props: {},
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const store = useStore()
    const state = reactive({
      createModal: false,
      createModalTitle: '',
      currentEditGroup: null as TraderRequisiteGroupDTO|null,
      groups: [] as TraderRequisiteGroupDTO[],
      trafficGroups: [] as TrafficGroupDTO[]
    });
    const createForm = ref<InstanceType<typeof RequisiteGroupForm> | null>(null)


    const load = async () => {
      loader.value?.show()
      state.groups = Object.assign([], await store.dispatch("reqGroups/loadList"));
      state.trafficGroups = await api.call.trafficGroups.all()
      loader.value?.hide()
    }

    const add = () => {
      createForm.value?.setForm({
        ...defaultForm,
      } as RequisiteGroupFormDTO)
      state.createModalTitle = '';
      state.createModal = true
    }

    const edit = (item: TraderRequisiteGroupDTO) => {
      createForm.value?.setForm({
        ...defaultForm,
        ...item,
        trafficGroups: !item.trafficGroupIds ? [] : state.trafficGroups.filter(tg => item.trafficGroupIds?.includes(tg.id))
      } as RequisiteGroupFormDTO)
      state.createModalTitle = '';
      state.createModal = true
    }

    const remove = async (item: TraderRequisiteGroupDTO) => {
      loader.value?.show()
      await api.traderRequisiteGroupDelete(item.id);
      await load()
      loader.value?.hide()
    }

    const saveModal = async () => {
      loader.value?.show()
      try {
        await createForm.value?.save()
        state.createModal = false
        await load()
      } catch (e) {
        loader.value?.hide()
      }
    }

    const saveRow = async (index: number) => {
      const device = state.groups[index]
      loader.value?.show()
      try {
        if (!device.id) {
          const data = {
            name: device.name ?? '',
            trafficGroupIds: device.trafficGroupIds ?? null,
          } as RequisiteGroupUpdateRequestDTO
          state.groups[index] = await api.traderRequisiteGroupCreate(data)
        } else if (state.currentEditGroup?.id === device.id) {
          const g = state.currentEditGroup
          const data = {
            name: g.name ?? '',
            trafficGroupIds: g.trafficGroupIds ?? null,
          } as RequisiteGroupUpdateRequestDTO
          state.groups[index] = await api.traderRequisiteGroupUpdate(g.id, data)
          state.currentEditGroup = null
        } else {
          loader.value?.hide()
          return
        }
        loader.value?.hide()
        await load()
      } catch (e) {
        loader.value?.hide()
      }
    }

    const toggleRequisites = async (id: number, isActive: boolean) => {
      loader.value?.show()
      await api.traderRequisiteGroupToggleRequisites(id, isActive ? 'activate' : 'deactivate')
      notification.notify({title: isActive ? helper.trans("labels.requisites_activated") : helper.trans("labels.requisites_deactivated")})
      loader.value?.hide()
      await load()
    }

    const cancel = (index: any) => {
      const device = state.groups[index];

      if (state.currentEditGroup && device.id === state.currentEditGroup.id) {
        state.currentEditGroup = null;

        return;
      }

      state.groups = state.groups.filter((item: TraderRequisiteGroupDTO, dIndex: number) => dIndex !== index);
    }

    return {
      ...toRefs(state),
      loader,
      load,
      add,
      cancel,
      edit,
      remove,
      saveModal,
      saveRow,
      toggleRequisites,
      isTrader: computed(() => store.getters.isTrader),
      isTraderOperator: computed(() => store.getters.isTraderOperator),
      createForm,
      helper
    }
  }
})
</script>
